export interface ElementLayoutSnapshot {
  label: string;
  exists: boolean;
  tagName?: string;
  dataName?: string | null;
  dataAccordionTitle?: string | null;
  id?: string;
  className?: string | null;
  rectTop?: number;
  rectBottom?: number;
  rectWidth?: number;
  rectHeight?: number;
  clientWidth?: number;
  clientHeight?: number;
  scrollWidth?: number;
  scrollHeight?: number;
  scrollTop?: number;
  scrollLeft?: number;
  offsetWidth?: number;
  offsetHeight?: number;
  display?: string;
  position?: string;
  flex?: string;
  flexDirection?: string;
  flexGrow?: string;
  flexShrink?: string;
  width?: string;
  height?: string;
  minWidth?: string;
  minHeight?: string;
  maxHeight?: string;
  overflowX?: string;
  overflowY?: string;
  paddingTop?: string;
  paddingBottom?: string;
  maskImage?: string;
  webkitMaskImage?: string;
  contain?: string;
  transform?: string;
  clipPath?: string;
  boxShadow?: string;
  borderRadius?: string;
  borderBottomWidth?: string;
  childElementCount?: number;
}

export interface ClippingAncestorReport {
  scrollableRectBottom: number;
  scrollableClientBottom: number;
  lastChildRectBottom: number | null;
  firstClippingAncestorBelow: {
    found: true;
    depth: number;
    tagName: string;
    dataName: string | null;
    className: string | null;
    overflowY: string;
    contain: string;
    rectBottom: number;
    pixelsClippingScrollableBottom: number;
    pixelsClippingLastChildBottom: number | null;
  } | { found: false };
}

export interface ElementHierarchyInput {
  element: HTMLElement | null;
  label: string;
  maxDepth?: number;
}

const DEFAULT_HIERARCHY_DEPTH = 8;
const ROUNDING_FACTOR = 100;
const HTML_CLASS_ATTRIBUTE = 'class';
const DATA_ACCORDION_TITLE_ATTRIBUTE = 'data-accordion-title';
const WEBKIT_MASK_IMAGE_PROPERTY = '-webkit-mask-image';

enum LayoutOverflowValue {
  Auto = 'auto',
  Clip = 'clip',
  Hidden = 'hidden',
  Scroll = 'scroll',
}

enum LayoutContainValue {
  Paint = 'paint',
  Strict = 'strict',
}

enum LayoutClipPathValue {
  None = 'none',
}

function roundLayoutNumber(value: number): number {
  return Math.round(value * ROUNDING_FACTOR) / ROUNDING_FACTOR;
}

function getElementClassName(element: HTMLElement): string | null {
  return element.getAttribute(HTML_CLASS_ATTRIBUTE);
}

export function captureElementLayoutSnapshot({
  element,
  label,
}: ElementHierarchyInput): ElementLayoutSnapshot {
  if (!element) {
    return {
      label,
      exists: false,
    };
  }

  const rect = element.getBoundingClientRect();
  const computedStyle = window.getComputedStyle(element);

  return {
    label,
    exists: true,
    tagName: element.tagName.toLowerCase(),
    dataName: element.dataset.name ?? null,
    dataAccordionTitle: element.getAttribute(DATA_ACCORDION_TITLE_ATTRIBUTE),
    id: element.id || undefined,
    className: getElementClassName(element),
    rectTop: roundLayoutNumber(rect.top),
    rectBottom: roundLayoutNumber(rect.bottom),
    rectWidth: roundLayoutNumber(rect.width),
    rectHeight: roundLayoutNumber(rect.height),
    clientWidth: element.clientWidth,
    clientHeight: element.clientHeight,
    scrollWidth: element.scrollWidth,
    scrollHeight: element.scrollHeight,
    scrollTop: element.scrollTop,
    scrollLeft: element.scrollLeft,
    offsetWidth: element.offsetWidth,
    offsetHeight: element.offsetHeight,
    display: computedStyle.display,
    position: computedStyle.position,
    flex: computedStyle.flex,
    flexDirection: computedStyle.flexDirection,
    // eslint-disable-next-line local-rules/no-conflicting-style-shorthand -- telemetry snapshot of computed styles, not an applied style object
    flexGrow: computedStyle.flexGrow,
    // eslint-disable-next-line local-rules/no-conflicting-style-shorthand -- telemetry snapshot of computed styles, not an applied style object
    flexShrink: computedStyle.flexShrink,
    width: computedStyle.width,
    height: computedStyle.height,
    minWidth: computedStyle.minWidth,
    minHeight: computedStyle.minHeight,
    maxHeight: computedStyle.maxHeight,
    overflowX: computedStyle.overflowX,
    overflowY: computedStyle.overflowY,
    paddingTop: computedStyle.paddingTop,
    paddingBottom: computedStyle.paddingBottom,
    maskImage: computedStyle.maskImage,
    webkitMaskImage: computedStyle.getPropertyValue(WEBKIT_MASK_IMAGE_PROPERTY),
    contain: computedStyle.contain,
    transform: computedStyle.transform,
    clipPath: computedStyle.clipPath,
    boxShadow: computedStyle.boxShadow,
    borderRadius: computedStyle.borderRadius,
    borderBottomWidth: computedStyle.borderBottomWidth,
    childElementCount: element.childElementCount,
  };
}

function ancestorClipsBelow(style: CSSStyleDeclaration): boolean {
  if (
    style.overflowY === LayoutOverflowValue.Hidden ||
    style.overflowY === LayoutOverflowValue.Clip ||
    style.overflowY === LayoutOverflowValue.Auto ||
    style.overflowY === LayoutOverflowValue.Scroll
  ) {
    return true;
  }
  if (
    style.contain.includes(LayoutContainValue.Paint) ||
    style.contain.includes(LayoutContainValue.Strict)
  ) {
    return true;
  }
  if (style.clipPath && style.clipPath !== LayoutClipPathValue.None) {
    return true;
  }
  return false;
}

export function findFirstClippingAncestorBelow({
  scrollable,
}: {
  scrollable: HTMLElement | null;
}): ClippingAncestorReport | null {
  if (!scrollable) {
    return null;
  }
  const scrollableRect = scrollable.getBoundingClientRect();
  const scrollableClientBottom = scrollableRect.top + scrollable.clientHeight;
  const lastChildElement = scrollable.lastElementChild instanceof HTMLElement
    ? scrollable.lastElementChild
    : null;
  const lastChildRect = lastChildElement?.getBoundingClientRect() ?? null;

  const baseReport: ClippingAncestorReport = {
    scrollableRectBottom: roundLayoutNumber(scrollableRect.bottom),
    scrollableClientBottom: roundLayoutNumber(scrollableClientBottom),
    lastChildRectBottom: lastChildRect ? roundLayoutNumber(lastChildRect.bottom) : null,
    firstClippingAncestorBelow: { found: false },
  };

  let parent = scrollable.parentElement;
  let depth = 1;
  while (parent) {
    const style = window.getComputedStyle(parent);
    if (ancestorClipsBelow(style)) {
      const parentRect = parent.getBoundingClientRect();
      return {
        ...baseReport,
        firstClippingAncestorBelow: {
          found: true,
          depth,
          tagName: parent.tagName.toLowerCase(),
          dataName: parent.dataset.name ?? null,
          className: getElementClassName(parent),
          overflowY: style.overflowY,
          contain: style.contain,
          rectBottom: roundLayoutNumber(parentRect.bottom),
          pixelsClippingScrollableBottom: roundLayoutNumber(scrollableRect.bottom - parentRect.bottom),
          pixelsClippingLastChildBottom: lastChildRect
            ? roundLayoutNumber(lastChildRect.bottom - parentRect.bottom)
            : null,
        },
      };
    }
    parent = parent.parentElement;
    depth += 1;
  }

  return baseReport;
}

export function captureElementHierarchy({
  element,
  label,
  maxDepth = DEFAULT_HIERARCHY_DEPTH,
}: ElementHierarchyInput): ElementLayoutSnapshot[] {
  const snapshots: ElementLayoutSnapshot[] = [];
  let currentElement = element;
  let depth = 0;

  while (currentElement && depth < maxDepth) {
    snapshots.push(captureElementLayoutSnapshot({
      element: currentElement,
      label: `${label}:${depth}`,
    }));
    currentElement = currentElement.parentElement;
    depth += 1;
  }

  return snapshots;
}
